/**
 * OwnerSalesDocsPanel — Owner-only catalogue of strategy / sales / migration
 * documents. Lists each .docx with title, track tag, description, size,
 * last-modified, and an audit-logged Download button.
 *
 * Source: GET /api/owner/sales-docs
 * Download: GET /api/owner/sales-docs/{slug}/download  (audit-logged)
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, AlertCircle, BookOpen } from "lucide-react";

const TRACK_TONE = {
  Partner: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  Strategy: "bg-purple-50 text-purple-700 border-purple-200",
  Azure: "bg-sky-50 text-sky-700 border-sky-200",
  AWS: "bg-amber-50 text-amber-700 border-amber-200",
};

// Track render order — Partner first because it's the newest & most surfaced.
const TRACK_ORDER = ["Partner", "Strategy", "Azure", "AWS"];

const MIME_BY_EXT = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  pdf: "application/pdf",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
};

function fmtSize(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 1) return "today";
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function OwnerSalesDocsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingSlug, setDownloadingSlug] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const r = await api.get("/owner/sales-docs");
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async (entry) => {
    if (!entry.available) {
      toast.error(`Not built yet. Regenerate first.`);
      return;
    }
    setDownloadingSlug(entry.slug);
    try {
      const res = await api.get(`/owner/sales-docs/${encodeURIComponent(entry.slug)}/download`, {
        responseType: "blob",
      });
      const ext = (entry.filename.split(".").pop() || "").toLowerCase();
      const blob = new Blob([res.data], {
        type: MIME_BY_EXT[ext] || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = entry.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${entry.title}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Download failed");
    } finally {
      setDownloadingSlug(null);
    }
  };

  return (
    <section
      id="sales-docs"
      className="mt-6 scroll-mt-24 rounded-2xl border-2 border-fuchsia-100 bg-gradient-to-br from-white to-fuchsia-50/30 p-6 shadow-sm"
      data-testid="owner-sales-docs-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-ek-purple/10">
            <BookOpen className="h-4 w-4 text-ek-purple" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Sales, partner & strategy documents
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Board decks, partner packs, migration papers. Owner-only. Every
              download is audit-logged. Files are backend-served — no copies
              in the public folder.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-ek-ink hover:bg-black/5"
          data-testid="owner-sales-docs-refresh"
        >
          <RefreshCw className="mr-1 inline h-3 w-3" /> Refresh
        </button>
      </div>

      {loading && (
        <p className="mt-4 text-sm text-ek-ink/60" data-testid="owner-sales-docs-loading">
          Loading…
        </p>
      )}
      {err && !loading && (
        <p className="mt-4 text-sm text-red-600" data-testid="owner-sales-docs-error">
          {err}
        </p>
      )}

      {!loading && !err && data && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Documents in catalogue" value={data.totals.count}
                  testid="owner-sales-docs-count" />
            <Stat label="Built & available" value={data.totals.available} tone="green"
                  testid="owner-sales-docs-available" />
            <Stat label="Missing (need rebuild)" value={data.totals.missing}
                  tone={data.totals.missing > 0 ? "amber" : "neutral"}
                  testid="owner-sales-docs-missing" />
          </div>

          {data.totals.missing > 0 && (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
              data-testid="owner-sales-docs-missing-warning"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Some documents have not been built in this environment. Run the
                regen command shown on each card.
              </span>
            </div>
          )}

          <div className="mt-4 space-y-5" data-testid="owner-sales-docs-groups">
            {(() => {
              const groups = {};
              (data.items || []).forEach((e) => {
                (groups[e.track] = groups[e.track] || []).push(e);
              });
              const ordered = [
                ...TRACK_ORDER.filter((t) => groups[t]),
                ...Object.keys(groups).filter((t) => !TRACK_ORDER.includes(t)),
              ];
              return ordered.map((track) => (
                <div key={track} data-testid={`owner-sales-docs-group-${track.toLowerCase()}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                        TRACK_TONE[track] || "border-black/10 bg-black/5 text-ek-ink/60"
                      }`}
                    >
                      {track}
                    </span>
                    <span className="text-[11px] text-ek-ink/50">
                      {groups[track].length} document{groups[track].length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="grid gap-3">
                    {groups[track].map((entry) => (
              <article
                key={entry.slug}
                className="rounded-xl border border-black/5 bg-black/[0.015] p-4"
                data-testid={`owner-sales-doc-${entry.slug}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-ek-purple shadow-sm">
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-sm font-semibold text-ek-ink">
                          {entry.title}
                        </h3>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            TRACK_TONE[entry.track] || "border-black/10 bg-black/5 text-ek-ink/60"
                          }`}
                          data-testid={`owner-sales-doc-track-${entry.slug}`}
                        >
                          {entry.track}
                        </span>
                        {!entry.available && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            Not built
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs italic text-ek-ink/55">{entry.subtitle}</p>
                      <p className="mt-2 text-sm text-ek-ink/75">{entry.description}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ek-ink/50">
                        <span>Size: {fmtSize(entry.size_bytes)}</span>
                        <span>Modified: {fmtAge(entry.modified_at)}</span>
                        <span className="truncate" title={entry.filename}>
                          {entry.filename}
                        </span>
                      </div>
                      {!entry.available && (
                        <code
                          className="mt-2 block break-all rounded bg-black/[0.04] px-2 py-1 text-[11px] text-ek-ink/70"
                          data-testid={`owner-sales-doc-regen-${entry.slug}`}
                        >
                          {entry.regen_cmd}
                        </code>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => download(entry)}
                    disabled={!entry.available || downloadingSlug === entry.slug}
                    className="ek-btn inline-flex shrink-0 items-center gap-1.5 disabled:opacity-50"
                    data-testid={`owner-sales-doc-download-${entry.slug}`}
                  >
                    {downloadingSlug === entry.slug ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Download
                  </button>
                </div>
              </article>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </section>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneClass = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    neutral: "text-ek-ink",
  }[tone || "neutral"];
  return (
    <div className="rounded-xl border border-black/5 bg-black/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-ek-ink/55">{label}</div>
      <div className={`mt-0.5 text-base font-semibold ${toneClass}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

export default OwnerSalesDocsPanel;
